import countryEmoji from 'country-emoji';
import { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../../contexts/AuthContext";
import supabase from "../../services/supabase";
import "./Leaderboards.css";

// Utility to convert country name to emoji flag
function countryToFlag(country) {
  return countryEmoji.flag(country) || '🌐';
}

// Helper to get username from user object or fetch if needed
const goToProfile = async (user, navigate) => {
  if (user.username) {
    navigate(`/profile/${user.username}`);
  } else if (user.user_id) {
    // Fetch username from user_profiles
    const { data, error } = await supabase
      .from('user_profiles')
      .select('username')
      .eq('user_id', user.user_id)
      .single();
    if (data && data.username) {
      navigate(`/profile/${data.username}`);
    } else {
      navigate('/profile');
    }
  } else {
    navigate('/profile');
  }
};

const TABS = [
  { key: 'daily', label: '🕒 Daily' },
  { key: 'weekly', label: '📅 Weekly' },
  { key: 'monthly', label: '🗓️ Monthly' },
  { key: 'all', label: '🏆 All-Time' },
];

function getTimeframeRange(tabKey) {
  const now = new Date();
  let start, end;
  end = now;
  if (tabKey === 'daily') {
    start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  } else if (tabKey === 'weekly') {
    // Start of week (Sunday)
    const day = now.getDay();
    start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
  } else if (tabKey === 'monthly') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
  } else {
    // All-Time: use a very early date
    start = new Date(2000, 0, 1); // Jan 1, 2000
  }
  return { start, end };
}

export default function Leaderboards() {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('all');
  const [currentUserHasPosts, setCurrentUserHasPosts] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check if current user has posts
  useEffect(() => {
    async function checkCurrentUserPosts() {
      if (user) {
        const { data, error } = await supabase
          .from('posts')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);
        
        if (!error && data && data.length > 0) {
          setCurrentUserHasPosts(true);
        } else {
          setCurrentUserHasPosts(false);
        }
      }
    }
    
    checkCurrentUserPosts();
  }, [user]);

  useEffect(() => {
  let cancelled = false;

    async function fetchLeaderboard() {
      setLoading(true);
      try {
        const { start, end } = getTimeframeRange(selectedTab);

        const { data: postCounts, error: postError } = await supabase
          .rpc("get_top_posters", {
            start_date: start.toISOString(),
            end_date: end.toISOString(),
          });

        if (postError) {
          console.error("Error getting top posters:", postError);
          if (!cancelled) {
            setLeaderboardData([]);
            setLoading(false);
          }
          return;
        }

        if (!postCounts || postCounts.length === 0) {
          if (!cancelled) {
            setLeaderboardData([]);
            setLoading(false);
          }
          return;
        }

        const userIds = postCounts.map((u) => u.user_id);
        const [profilesRes, favCountriesRes] = await Promise.all([
          supabase
            .from("user_profiles")
            .select(
              "user_id, username, profilePicture, destination_count, follower_count"
            )
            .in("user_id", userIds),
          supabase
            .from("new_post")
            .select("user_id, country")
            .in("user_id", userIds)
            .not("country", "is", null)
            .gte("created_at", start.toISOString())
            .lte("created_at", end.toISOString()),
        ]);

        if (profilesRes.error) {
          console.error("Error fetching profiles:", profilesRes.error);
        }

        if (favCountriesRes.error) {
          console.error("Error fetching fav countries:", favCountriesRes.error);
        }

        const profiles = profilesRes.data || [];
        const favRows = favCountriesRes.data || [];
        const profileMap = new Map(
          profiles.map((p) => [p.user_id, p])
        );

        const favMap = new Map();
        for (const row of favRows) {
          if (!row.country) continue;
          const prev = favMap.get(row.user_id) || [];
          if (!prev.includes(row.country)) {
            prev.push(row.country);
            favMap.set(row.user_id, prev);
          }
        }
        
        const users = postCounts.map((u) => {
          const profile = profileMap.get(u.user_id) || {};
          return {
            ...profile,
            user_id: u.user_id,
            post_count: u.post_count,
            favCountries: favMap.get(u.user_id) || [],
          };
        });

        if (!cancelled) {
          setLeaderboardData(users);
          setLoading(false);
        }
      } catch (err) {
        console.error("Unexpected error fetching leaderboard:", err);
        if (!cancelled) {
          setLeaderboardData([]);
          setLoading(false);
        }
      }
    }

    fetchLeaderboard();

    return () => {
      cancelled = true;
    };
  }, [selectedTab]);


  return (
    <div className="leaderboards-container">
      <h2 className="leaderboards-title">Leaderboards</h2>
      <div className="leaderboards-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`leaderboards-tab${selectedTab === tab.key ? ' active' : ''}`}
            onClick={() => setSelectedTab(tab.key)}
            disabled={loading && selectedTab === tab.key}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="leaderboards-table-wrapper">
        <table className="leaderboards-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Pioneer</th>
              <th>Posts</th>
              <th>Destinations</th>
              <th>Followers</th>
              <th>Fav spots</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6}>Loading...</td>
              </tr>
            ) : leaderboardData.length === 0 ? (
              <tr>
                <td colSpan={6}>No data available</td>
              </tr>
            ) : (
              leaderboardData.map((leaderboardUser, idx) => (
                <tr 
                  key={leaderboardUser.user_id}
                  className={user && leaderboardUser.user_id === user.id ? 'leaderboard-row-current-user' : ''}
                >
                  <td>{idx + 1}</td>
                  <td className="pioneer-cell">
                    <span
                      className="profile-pic-clickable"
                      onClick={() => goToProfile(leaderboardUser, navigate)}
                      tabIndex={0}
                      role="button"
                      aria-label={`View ${leaderboardUser.username}'s profile`}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') goToProfile(leaderboardUser, navigate); }}
                    >
                      {leaderboardUser.profilePicture ? (
                        <img
                          src={leaderboardUser.profilePicture}
                          alt={leaderboardUser.username}
                          className="profile-pic"
                          onError={e => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/150?text=Profile';
                          }}
                        />
                      ) : (
                        <div className="profile-pic-placeholder">
                          {leaderboardUser.username ? leaderboardUser.username.charAt(0).toUpperCase() : '?'}
                        </div>
                      )}
                    </span>
                    <span
                      className="leaderboard-username leaderboard-username-clickable"
                      onClick={() => goToProfile(leaderboardUser, navigate)}
                      tabIndex={0}
                      role="button"
                      aria-label={`View ${leaderboardUser.username}'s profile`}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') goToProfile(leaderboardUser, navigate); }}
                    >
                      {leaderboardUser.username}
                    </span>
                  </td>
                  <td>{leaderboardUser.post_count ?? 0}</td>
                  <td>{leaderboardUser.destination_count ?? 0}</td>
                  <td>{leaderboardUser.follower_count ?? 0}</td>
                  <td className="fav-spots-cell">
                    {leaderboardUser.favCountries && leaderboardUser.favCountries.length > 0
                      ? leaderboardUser.favCountries.slice(0, 3).map((country, i) => (
                          <span key={i} className="country-flag" title={country}>{countryToFlag(country)}</span>
                        ))
                      : <span className="fav-spot-badge">—</span>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Join the Leaderboard CTA for users with no activity */}
      {user && !loading && !currentUserHasPosts && !leaderboardData.some(leaderboardUser => leaderboardUser.user_id === user.id) && (
        <div className="join-leaderboard-cta">
          <span className="cta-icon">🚀</span>
          <h3>Join the Leaderboard!</h3>
          <p>Start posting to climb the leaderboard and see yourself here!</p>
        </div>
      )}
    </div>
  );
} 